import { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import { getColumnsLiabilityInsurance } from "./columns";
import {
  LiabilityInsurance,
  LiabilityInsuranceEdge,
  PaginatedLiabilityInsuranceQuery,
  PaginatedLiabilityInsuranceQueryVariables,
  PaginatedLiabilityInsuranceDocument
} from "@/graphql/__generated__/graphql";
import { useRemoveLiabilityInsuranceMutationHook } from "@/hooks";
import useUserStore from "@/store/shield";
import AddLiabilityInsuranceDialog from "./AddLiabilityInsuranceDialog";
import LiabilityInsuranceFilter from "./LiabilityInsuranceFilter";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedLiabilityInsuranceQueryVariables>) => void;
}

/**
 * LiabilityInsuranceComponent component displays a table of liability insurance configurations.
 * It allows filtering and sorting of liability insurance records, and provides a dialog for adding new configurations.
 * It also allows removing liability insurance configurations.
 *
 * @component
 * @example
 * <LiabilityInsuranceComponent />
 */
function LiabilityInsuranceComponent() {

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeLiabilityInsuranceMutation } = useRemoveLiabilityInsuranceMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedLiabilityInsurance";

  // State management
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isRemoveLiabilityInsuranceOpen, setIsRemoveLiabilityInsuranceOpen] = useState(false);
  const [selectedLiabilityInsurance, setSelectedLiabilityInsurance] = useState<LiabilityInsurance | null>(null);

  const onFilterSubmit = (variables: PaginatedLiabilityInsuranceQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (liabilityInsurance: LiabilityInsurance) => {
    setSelectedLiabilityInsurance(liabilityInsurance);
    setIsRemoveLiabilityInsuranceOpen(true);
  };

  const handleRemoveLiabilityInsurance = (liabilityInsurance: LiabilityInsurance) => {
    removeLiabilityInsuranceMutation({ variables: { removeLiabilityInsuranceId: liabilityInsurance.id } }).finally(() => {
      setIsRemoveLiabilityInsuranceOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsLiabilityInsurance(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<LiabilityInsuranceEdge, PaginatedLiabilityInsuranceQuery, PaginatedLiabilityInsuranceQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedLiabilityInsuranceDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-between">
              <LiabilityInsuranceFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddDialogOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>

      {/* Add Liability Insurance Dialog */}
      <AddLiabilityInsuranceDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      />

      {/* Remove Liability Insurance Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveLiabilityInsuranceOpen}
        onOpenChange={setIsRemoveLiabilityInsuranceOpen}
        actionButtonFn={() => handleRemoveLiabilityInsurance(selectedLiabilityInsurance as LiabilityInsurance)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removeliabilityinsurance")}
      />
    </>
  );
}

export default LiabilityInsuranceComponent;
